import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';

import { daysFromNow } from '@zapier/date';

import { useIsAuthenticated } from '@zapier/design-system-revenue/src/hooks';

import { companyTrialBanner as CompanyTrialBannerQuery } from './types/companyTrialBanner';

export const useCompanyTrialBannerQuery = gql`
  query companyTrialBanner(
    $accountId: Int!
    $interval: BillingInterval!
    $type: String!
  ) {
    account(id: $accountId) {
      companiesTrialEndDate
      currentPlan: planV2 {
        id
        interval
        name
      }
      currentUserRole
      id
    }

    nextPlan: nextPlanV2(
      accountId: $accountId
      interval: $interval
      type: $type
    ) {
      id
      name
    }

    profile: currentUserV2 {
      id
    }
  }
`;

export type Props = {
  skip?: boolean;
};

export const useData = ({ skip }: Props = {}) => {
  const { isAuthenticated } = useIsAuthenticated();
  const accountId = localStorage.getItem('currentAccountId');

  const { data, loading, error } = useQuery<CompanyTrialBannerQuery>(
    useCompanyTrialBannerQuery,
    {
      skip: skip || !isAuthenticated || !accountId,
      variables: {
        accountId: parseInt(accountId ?? ''),
        interval: 'year',
        type: 'Company',
      },
    }
  );

  const currentPlan = data?.account?.currentPlan;
  const nextPlan = data?.nextPlan;
  const trialEndDate = data?.account.companiesTrialEndDate;

  return {
    accountId,
    currentPlan: currentPlan
      ? {
          name: currentPlan.name,
        }
      : undefined,
    error: error
      ? 'There was an error fetching account information. Please try again.'
      : null,
    isAccountOwner: data?.account?.currentUserRole === 'owner',
    isLoading: loading,
    nextPlan:
      currentPlan && nextPlan
        ? {
            interval: currentPlan.interval,
            name: nextPlan.name,
          }
        : undefined,
    profileId: data?.profile?.id,
    trialDaysRemaining: trialEndDate ? daysFromNow(trialEndDate) : 0,
  };
};
